/**
 * Analytics Provider
 *
 * Provides centralized analytics tracking for the application.
 * Handles navigation tracking and other app-wide analytics concerns.
 */

import type { PropsWithChildren } from "react";
import React, { useEffect, useRef } from "react";
import { usePathname } from "expo-router";
import { trackEvent } from "@src/services/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@src/services/analytics/events";

export const AnalyticsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const pathname = usePathname();
  const previousPathnameRef = useRef<string | null>(null);

  // Track all navigation events (screen views) centrally
  useEffect(() => {
    // Skip tracking on initial mount or if pathname hasn't changed
    if (previousPathnameRef.current === pathname) {
      return;
    }

    // Track the screen view
    trackEvent(ANALYTICS_EVENTS.SCREEN_VIEWED, {
      screen_path: pathname,
      previous_screen_path: previousPathnameRef.current || undefined,
    });

    // Update the previous pathname
    previousPathnameRef.current = pathname;
  }, [pathname]);

  return <>{children}</>;
};
